import { React18Block } from "./React18Block";
import { useManifest } from "./useManifest";
import { BlockDefinition, PAGE_RENDERER_CONSTANTS } from "@softr-io/blocks-sdk";
import { BlockLoader } from "./BlockLoader";
import { ChildBlockProps } from "@softr-io/blocks-sdk/react";
import { useAppConstants } from "./AppConstantsContext";
import { useState } from "react";
import clsx from "clsx";
import { blockRootDataRootAttribute, DYNAMIC_HEIGHT_BLOCKS } from "./block-root";
import { PARENT_SELECTOR_ATTRIBUTE_NAME } from "./parent-selector";

type Props = Readonly<{
    definition: BlockDefinition;
    childBlocks: Readonly<Record<BlockDefinition["id"], BlockDefinition>>;
    topLevel: boolean;
    childBlockRootProps: ChildBlockProps["wrapperProps"] | null;
    hiddenDueToPreviousBlockLoading?: boolean;
}>;
/**
 * A dynamic block is one that is rendered by dynamically loaded javascript.
 * It is typically a react block, but doesn't have to be.
 * It's renderer is determined by the manifest.
 */
export function DynamicBlock({
    definition,
    childBlocks,
    topLevel,
    childBlockRootProps,
    hiddenDueToPreviousBlockLoading,
}: Props) {
    const { baseUrl, mode, blockIdAttributeName } = useAppConstants();
    const manifest = useManifest({
        name: definition.name,
        version: definition.version,
        baseUrl,
        enabled: !hiddenDueToPreviousBlockLoading,
    });
    const allowLoader = mode === "studio";

    const scopeClassName = manifest?.version === "2" ? manifest.cssScopeClassName : "";
    const isNavBlock = definition.name === "navigation";

    // It might look confusing that we're storing DOM elements in state instead of refs, but
    // this is correct. The setting of these nav roots effects the rendering of the navigation block.
    // i.e. setTopbarRoot *must* cause a re-render.
    const [topbarRoot, setTopbarRoot] = useState<HTMLDivElement | null>(null);
    const [sidebarRoot, setSidebarRoot] = useState<HTMLDivElement | null>(null);
    const [bottombarRoot, setBottombarRoot] = useState<HTMLDivElement | null>(null);

    const isHorizontalHeader =
        definition.name.toLowerCase().startsWith("header") && !definition.elements.verticalEnabled;

    const blockRootAttrs: Record<string, string> = {
        "data-block": blockRootDataRootAttribute(definition),
        "data-spr-block-id": definition.id,
        category: definition.category,

        // Apply a unique attribute to the block root so that the
        // blocks can apply styles to the block root itself,
        // rather than the nodes that `Component` renders.
        // For example, check out the header block, which uses
        // useHeadStyle with parentSelector value to make the
        // header sticky (since the outer div isn't rendered by the component)
        // The query selector should match exactly 1 DOM element, which
        // is the block root for *this* block. It should not mat
        [PARENT_SELECTOR_ATTRIBUTE_NAME]: definition.id,
    };
    if (topLevel && blockIdAttributeName) {
        blockRootAttrs[blockIdAttributeName] = definition.id;
    }
    return (
        <>
            {isNavBlock && (
                // Nav blocks don't need to respect hiddenDueToPreviousBlockLoading because they're always
                // on the top of the page,
                // so we don't need to block their rendering because the blocks before aren't loaded
                // In fact, in AppRoot, we don't pass this flag to nav blocks at all.
                <>
                    <div id="topbar-root" className={scopeClassName} ref={setTopbarRoot} />
                    <div id="sidebar-root" className={scopeClassName} ref={setSidebarRoot} />
                    <div id="bottombar-root" className={scopeClassName} ref={setBottombarRoot} />
                </>
            )}
            <div
                id={definition.hrid}
                className={clsx(
                    scopeClassName,
                    definition.name === "navigation" && "spr-navigation-placeholder",

                    DYNAMIC_HEIGHT_BLOCKS.includes(definition.name) && "dynamic-height",

                    isHorizontalHeader && PAGE_RENDERER_CONSTANTS.HORIZONTAL_HEADER_CLASS_NAME,
                    childBlockRootProps?.className,
                    hiddenDueToPreviousBlockLoading && "spr-hidden",
                )}
                style={childBlockRootProps?.style}
                {...blockRootAttrs}
            >
                {!manifest ? (
                    allowLoader && <BlockLoader />
                ) : (
                    <React18Block
                        definition={definition}
                        childBlocks={childBlocks}
                        topLevel={topLevel}
                        childBlockRootProps={childBlockRootProps}
                        topbarRoot={topbarRoot}
                        sidebarRoot={sidebarRoot}
                        bottombarRoot={bottombarRoot}
                    />
                )}
            </div>
        </>
    );
}
